import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
} from '@mui/material';
import { Initiative } from '../../types/database';
import { useAuth } from '../../contexts/AuthContext';

interface InitiativeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (initiative: Omit<Initiative, 'id' | 'created_at' | 'updated_at'>) => void;
  initiative?: Initiative | null;
}

export const InitiativeModal: React.FC<InitiativeModalProps> = ({
  open,
  onClose,
  onSubmit,
  initiative,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'Active',
    start_date: '',
    end_date: '',
    contacts: [] as string[],
    tags: [] as string[],
    notes: [] as string[],
    user_id: user?.id || '',
    relationship_types: {} as Record<string, string>,
    related_initiatives: [] as string[]
  });

  useEffect(() => {
    if (initiative) {
      setFormData({
        name: initiative.name,
        description: initiative.description,
        status: initiative.status,
        start_date: initiative.start_date,
        end_date: initiative.end_date,
        contacts: initiative.contacts || [],
        tags: initiative.tags || [],
        notes: initiative.notes || [],
        user_id: initiative.user_id,
        relationship_types: initiative.relationship_types || {},
        related_initiatives: initiative.related_initiatives || []
      });
    } else {
      setFormData({
        name: '',
        description: '',
        status: 'Active',
        start_date: '',
        end_date: '',
        contacts: [],
        tags: [],
        notes: [],
        user_id: user?.id || '',
        relationship_types: {},
        related_initiatives: []
      });
    }
  }, [initiative, user]);

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = () => {
    if (!user?.id) {
      return; // Don't submit if user is not authenticated
    }
    const initiativeData = {
      name: formData.name.trim(),
      description: formData.description.trim() || '',
      status: formData.status || 'Active',
      start_date: formData.start_date || new Date().toISOString(),
      end_date: formData.end_date || formData.start_date || new Date().toISOString(),
      contacts: formData.contacts || [],
      tags: formData.tags || [],
      notes: formData.notes || [],
      user_id: user.id,
      relationship_types: formData.relationship_types,
      related_initiatives: formData.related_initiatives
    };
    onSubmit(initiativeData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initiative ? 'Edit Initiative' : 'Add New Initiative'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleFormChange}
            required
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleFormChange}
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            select
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleFormChange}
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
            <MenuItem value="On Hold">On Hold</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Start Date"
            name="start_date"
            type="date"
            value={formData.start_date}
            onChange={handleFormChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            fullWidth
            label="End Date"
            name="end_date"
            type="date"
            value={formData.end_date}
            onChange={handleFormChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={!formData.name || !user?.id}
        >
          {initiative ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 